import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PolicyType } from "./types";
import { useToast } from "@/hooks/use-toast";
import { usePolicyTypeOperations } from "./usePolicyTypeOperations";
import { PolicyTypeForm } from "./components/PolicyTypeForm";
import { usePolicyTypes } from "./hooks/usePolicyTypes";

interface PolicyTypeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: (open: boolean) => void;
  selectedPolicyType?: PolicyType | null;
}

export const PolicyTypeDialog = ({ 
  isOpen, 
  onClose, 
  onOpenChange, 
  selectedPolicyType 
}: PolicyTypeDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    sort_order: 0,
    policy_line_id: "",
  });
  
  const { createPolicyType, updatePolicyType } = usePolicyTypeOperations();
  const { refetch } = usePolicyTypes();
  const { toast } = useToast();

  useEffect(() => {
    if (selectedPolicyType) {
      setFormData({
        name: selectedPolicyType.name,
        description: selectedPolicyType.description || "",
        sort_order: selectedPolicyType.sort_order || 0,
        policy_line_id: selectedPolicyType.policy_line_id || "",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        sort_order: 0,
        policy_line_id: "",
      });
    }
  }, [selectedPolicyType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = selectedPolicyType 
      ? await updatePolicyType(selectedPolicyType.id, formData)
      : await createPolicyType(formData);

    if (result.success) {
      toast({
        title: "Success",
        description: `Policy type ${selectedPolicyType ? 'updated' : 'created'} successfully`,
      });
      // Immediately refetch after successful operation
      await refetch();
      onClose();
    } else {
      toast({
        title: "Error",
        description: `Failed to ${selectedPolicyType ? 'update' : 'create'} policy type`,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{selectedPolicyType ? "Edit" : "Add"} Policy Type</DialogTitle>
          <DialogDescription>
            {selectedPolicyType ? "Update the" : "Create a new"} policy type with its details.
          </DialogDescription>
        </DialogHeader>
        <PolicyTypeForm
          formData={formData}
          onSubmit={handleSubmit}
          onChange={(newData) => setFormData(prev => ({ ...prev, ...newData }))}
          onCancel={onClose}
          mode={selectedPolicyType ? "edit" : "create"}
        />
      </DialogContent>
    </Dialog>
  );
};